import { OverlayModule } from '@angular/cdk/overlay';
import { PlatformModule } from '@angular/cdk/platform';
import { PortalModule } from '@angular/cdk/portal';
import { CommonModule, Location } from '@angular/common';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { APP_INITIALIZER, ErrorHandler, Injectable, Injector, NgModule, NgZone, forwardRef } from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import { MatIconModule } from '@angular/material/icon';
import { MAT_LEGACY_FORM_FIELD_DEFAULT_OPTIONS as MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/legacy-form-field';
import { MatLegacyMenuModule as MatMenuModule } from '@angular/material/legacy-menu';
import { MatLegacySelectModule as MatSelectModule } from '@angular/material/legacy-select';
import { MatLegacySnackBarModule as MatSnackBarModule } from '@angular/material/legacy-snack-bar';
import { BrowserModule, HAMMER_GESTURE_CONFIG, HammerGestureConfig, HammerModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { Router } from '@angular/router';
import { ServiceWorkerModule } from '@angular/service-worker';

import * as hammerjs from 'hammerjs';
import { getFirebaseConfig, getFirebaseEmulatorEnabled, getVersion } from '@app/src/environment/index';
import { enableEmulator, firebase } from '@app/src/firebase';
import '@app/src/grpc-web-client-patch';
import { MingaFlexBreakPointsProvider } from '@app/src/styles/flex-breakpoints';
import { grpc } from '@improbable-eng/grpc-web';
import { waitForCordovaDeviceReady } from '@lib/cordova/device-ready';
import { EffectsModule } from '@ngrx/effects';
import { StoreRouterConnectingModule } from '@ngrx/router-store';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { TraceService } from '@sentry/angular';
import { GoogleTagManagerModule } from 'angular-google-tag-manager';
import { RealtimeDataConsumer, RealtimeDirectMessageConsumer, RealtimeNotificationConsumer } from 'libs/realtime';
import { TokenStatus } from 'libs/util';
import { QuillModule } from 'ngx-quill';
import 'quill';

import { BarcodeScanner } from '@app/src/app/barcodeScanner';
import { CordovaBarcodeScanner } from '@app/src/app/barcodeScanner/cordova';
import { WebcamBarcodeScanner } from '@app/src/app/barcodeScanner/webcam/WebcamBarcodeScanner';
import { MgButtonModule } from '@app/src/app/button';
import { DialogModule } from '@app/src/app/components/Dialog';
import { LoaderOverlayModule } from '@app/src/app/components/LoaderOverlay';
import { ContentStoreModule } from '@app/src/app/content-common/store/module';
import '@app/src/app/elements/MgMentionQuillModule';
import { createErrorHandler } from '@app/src/app/error-handler';
import { FileModule } from '@app/src/app/file';
import { FirebaseMessaging } from '@app/src/app/firebase/messaging';
import { FirebaseMessagingCordova } from '@app/src/app/firebase/messaging/firebase-cordova';
import { FirebaseMessagingWeb } from '@app/src/app/firebase/messaging/firebase-web';
import { FirebaseMessagingFirestoreFallback } from '@app/src/app/firebase/messaging/firestore-fallback';
import { WelcomeModule } from '@app/src/app/ftue/Welcome';
import { GroupsFacadeService } from '@app/src/app/groups/services';
import { MingaSnackIconModule } from '@app/src/app/minimal/components/MingaSnackIcon';
import { AnalyticsService } from '@app/src/app/minimal/services/Analytics';
import { AppConfigService } from '@app/src/app/minimal/services/AppConfig';
import { AuthService } from '@app/src/app/minimal/services/Auth';
import { DeviceUpdaterService } from '@app/src/app/minimal/services/DeviceUpdater';
import { MgModalModule } from '@app/src/app/minimal/services/MgModal';
import { MingaSnackModule } from '@app/src/app/minimal/services/MingaSnack';
import { SentryService } from '@app/src/app/minimal/services/Sentry/Sentry.service';
import { StartupService } from '@app/src/app/minimal/services/Startup/Startup.service';
import { MgModalContainerModule } from '@app/src/app/modal';
import { MessagingStoreModule } from '@app/src/app/modules/direct-message/store/module';
import { MgOverlayModule } from '@app/src/app/overlay';
import { PeopleModule } from '@app/src/app/people';
import { FirebaseMessagingRealtimeDataConsumer } from '@app/src/app/realtime';
import { RolesEffects } from '@app/src/app/roles/effects';
import { SearchOverlayModule } from '@app/src/app/search-overlay';
import { GradesEffects } from '@app/src/app/services/Grades';
import { UserStorage } from '@app/src/app/services/UserStorage';
import { ROUTER_DERIVED_STATE_GROUP_FETCHER } from '@app/src/app/state/tokens/routerDerivedStateGroupFetcher';
import { AuthStoreModule } from '@app/src/app/store/AuthStore';
import { MingaStoreFacadeService } from '@app/src/app/store/Minga/services';
import { RootEffects } from '@app/src/app/store/root/rootEffects';
import { RootEffectsModule } from '@app/src/app/store/root/rootEffects.module';
import { LinkDetailModule } from '@app/src/app/ugc-links/containers/LinkDetail';
import { URL_METADATA_STORAGE } from '@app/src/app/ugc-links/services';
import { createStorageInstance } from '@app/src/app/util/storage';

import { LayoutModule } from '@modules/layout';

import { ModalOverlayService } from '@shared/components/modal-overlay';
import { SwitchMingaModalModule } from '@shared/components/switch-minga-modal/switch-minga-modal.module';
import { SystemAlertModalModule } from '@shared/components/system-alert-modal';
import { SystemAlertSnackBarModule } from '@shared/components/system-alert-snackbar';
import { WebcamModalModule } from '@shared/components/webcam-modal';
import { AppRuntimeDefault, AppRuntimeInterface } from '@shared/services/app-runtime';
import { KioskPermissionsService } from '@shared/services/kiosk/kiosk-permissions.service';
import { FlexLayoutWorkaround } from '@shared/services/media/flex-layout-workaround.service';
import { UserpilotService } from '@shared/services/userpilot/Userpilot.service';

import { buildEnvironment } from '../environment/build';
import './MatIconRegistryPatch';
import { AppRoutingModule } from './app-routing.module';
import { CustomSerializer, appReducers, appRootStoreConfig } from './app-store';
import { AppComponent } from './app.component';
import { MG_MAT_FORM_FIELD_OPTIONS } from './constants';
import { IconModule } from './icon.module';
import { AuthInfoService } from './minimal/services/AuthInfo';
import { canUseWebPushApi } from './notifications/NotificationPermission.service';
import { patchCordovaBrowserTabPlugin } from './patchCordovaBrowserTabPlugin';
import { RealtimeEventHandlers } from './realtime-event/RealtimeEventHandlers';
import { RealtimeEvents } from './realtime-event/RealtimeEvents';
import { BranchService } from './shared/services/branch/branch.service';
import { MingaStoreModule } from './store/Minga';
import { startMultiOutletHack } from './util';

@Injectable()
export class MgHammerConfig extends HammerGestureConfig {
  overrides = {
    panright: { direction: hammerjs.DIRECTION_RIGHT },
    panleft: { direction: hammerjs.DIRECTION_LEFT },
    pandown: { direction: hammerjs.DIRECTION_DOWN },
    swipeleft: { direction: hammerjs.DIRECTION_LEFT },
    swiperight: { direction: hammerjs.DIRECTION_RIGHT },
    tap: { enable: true },
  };

  options = {
    // To allow stop propogations on taps
    domEvents: true,
  };

  public buildHammer(element: HTMLElement) {
    return new hammerjs(element);
  }
}

const setupInterceptor = (auth: AuthService, injector: Injector) => {
  window.addGrpcMetadataInterceptor({
    onBeforeSend: (metadata: grpc.Metadata) => {
      // trigger a refresh of the token if the one we currently have
      // is expired. Since getting a new token is async, if it's expired
      // we will have to settle for this request inevitably failing, but this
      // is at least a fallback to ensure it gets updated for the next request.
      auth.refreshTokenIfExpired();
      const token = auth.getLastIdToken();
      if (token) metadata.set('token', token);
      metadata.set('ngsw-bypass', 'true');
      metadata.set('mg-client-version', getVersion());
    },
    onReceive: (metadata: grpc.Metadata) => {
      if (!metadata.has('mg-status')) {
        return;
      }
      const status = parseInt(metadata.get('mg-status').toString(), 10);
      if (status === TokenStatus.VALID) {
        // All good, fam!
      } else if (status === TokenStatus.EXPIRED) {
        auth.authExpiredSnackbar();
      } else if (status === TokenStatus.MINGA_PAUSED) {
        const router: Router = injector.get(Router);
        const ngZone: NgZone = injector.get(NgZone);
        const location: Location = injector.get(Location);
        if (!location.path().startsWith('/landing')) {
          ngZone.run(() =>
            router.navigateByUrl('/paused/minga', {
              skipLocationChange: true,
            }),
          );
        }
      } else {
        console.warn('Unhandled TokenStatus', status);
      }
    },
  });
};

const appInitializer = (
  auth: AuthService,
  authInfo: AuthInfoService,
  appConfigService: AppConfigService,
  location: Location,
  injector: Injector,
  analytics: AnalyticsService,
  userStorage: UserStorage,
  messaging: FirebaseMessaging,
  sentry: SentryService,
  mingaStore: MingaStoreFacadeService,
  startupService: StartupService,
  userpilotService: UserpilotService,
  flexWorkaround: FlexLayoutWorkaround,
  realtimeEvents: RealtimeEvents,
  realtimeEventHandlers: RealtimeEventHandlers,
  branchService: BranchService,
) => {
  return async () => {
    try {
      await mingaStore.startup();
      userpilotService.initialize();
      startMultiOutletHack(location);
      await waitForCordovaDeviceReady();
      patchCordovaBrowserTabPlugin();
      const firebaseConfig = getFirebaseConfig();
      firebase.initializeApp(firebaseConfig);
      // need to manually initialize analytics on web
      if (window.MINGA_APP_BROWSER) {
        firebase.analytics();
      }
      const emulatorEnabled = getFirebaseEmulatorEnabled();
      if (emulatorEnabled) {
        console.log('Firebase emulator enabled');
        enableEmulator();
      }
      await appConfigService.startup();
      await messaging.init();
      userStorage.init();
      setupInterceptor(auth, injector);
      await auth.startup();
      await realtimeEvents.init();
      realtimeEventHandlers.init();

      const deviceUpdater: DeviceUpdaterService = injector.get(DeviceUpdaterService);
      deviceUpdater.startup();
      startupService.startup();
      flexWorkaround.initialize();
    } finally {
      const router = injector.get(Router);
      router.initialNavigation();
      // Initialize Branch after router is ready
      await branchService.initialize();
      await analytics.init(router);
    }
  };
};

const createTranslateLoader = (http: HttpClient) => {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
};

@NgModule({
  declarations: [AppComponent],
  imports: [
    LayoutModule,

    // Routing
    AppRoutingModule,

    // Minga dependencies
    IconModule,
    MgOverlayModule,
    MatIconModule,
    MatSnackBarModule,
    LoaderOverlayModule,
    MgModalContainerModule,
    MgModalModule,
    SearchOverlayModule,
    FileModule.forRoot(),
    RootEffectsModule,
    PeopleModule,
    MingaSnackIconModule,
    MingaSnackModule,
    LinkDetailModule,
    MgButtonModule,
    DialogModule,
    WelcomeModule,
    SystemAlertModalModule,
    SystemAlertSnackBarModule,
    WebcamModalModule,
    SwitchMingaModalModule,

    // External dependencies
    CommonModule,
    PlatformModule,
    MatSelectModule,
    PortalModule,
    FlexLayoutModule,
    BrowserModule,
    HttpClientModule,
    BrowserAnimationsModule,
    MatMenuModule,
    QuillModule.forRoot(),
    StoreModule.forRoot(appReducers, appRootStoreConfig),
    StoreDevtoolsModule.instrument({
      name: 'Minga store',
    }),
    StoreRouterConnectingModule.forRoot({ serializer: CustomSerializer }),
    EffectsModule.forRoot([RootEffects, RolesEffects, GradesEffects]),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpClient],
      },
    }),
    ServiceWorkerModule.register('/minga-sw.js', {
      enabled: buildEnvironment.production && window.MINGA_APP_BROWSER,
    }),
    MingaStoreModule,
    AuthStoreModule,
    MessagingStoreModule,
    ContentStoreModule,
    HammerModule,
    OverlayModule,
    GoogleTagManagerModule,
  ],
  providers: [
    {
      provide: AppRuntimeInterface,
      /**
       * Using a factory because window might not be defined in the early
       * stages of the build.
       *
       * Using 'any' here as a workaround because the runtime is temporarily
       * defining it's own Window interface. See notes on runtime.interface.ts
       * for why that is. When that Window interface is removed, this can be
       * updated to remove the any.
       */
      useFactory: () => {
        return new AppRuntimeDefault(window as any);
      },
    },
    {
      provide: ErrorHandler,
      useFactory: (router: Router) => {
        return createErrorHandler(router, {
          showDialog: false,
        });
      },
    },
    {
      provide: TraceService,
      deps: [Router],
      useValue: undefined,
    },
    {
      provide: ROUTER_DERIVED_STATE_GROUP_FETCHER,
      useClass: forwardRef(() => GroupsFacadeService),
    },
    {
      provide: APP_INITIALIZER,
      useFactory: appInitializer,
      multi: true,
      deps: [
        AuthService,
        AuthInfoService,
        AppConfigService,
        Location,
        Injector,
        AnalyticsService,
        UserStorage,
        FirebaseMessaging,
        TraceService,
        StartupService,
        MingaStoreFacadeService,
        UserpilotService,
        // @TODO remove once we get to angular v13+
        // hack flex layout service resolves: https://github.com/angular/flex-layout/issues/1201
        FlexLayoutWorkaround,
        RealtimeEvents,
        RealtimeEventHandlers,
        BranchService,
      ],
    },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: MG_MAT_FORM_FIELD_OPTIONS,
    },
    {
      provide: HAMMER_GESTURE_CONFIG,
      useClass: MgHammerConfig,
    },

    {
      provide: URL_METADATA_STORAGE,
      useFactory: () =>
        createStorageInstance({
          name: 'urlMetadata',
          // 5 minutes
          memoryStorageTimeout: 300000,
        }),
    },
    {
      provide: FirebaseMessaging,
      deps: [AuthInfoService, KioskPermissionsService],
      useFactory: (authInfo: AuthInfoService, kioskPermissions: KioskPermissionsService) => {
        const isWebPushApiEnabled = canUseWebPushApi();

        // If the user has explicitly denied push notifications the push api
        // will be unavailable. We resort to using the firestore fallback to
        // still give them real time notifications.
        if (window.MINGA_APP_BROWSER && isWebPushApiEnabled) {
          return new FirebaseMessagingWeb(kioskPermissions);
        } else if (!window.MINGA_APP_BROWSER) {
          return new FirebaseMessagingCordova(kioskPermissions);
        } else {
          console.warn('Using fallback for messaging');
          return new FirebaseMessagingFirestoreFallback(authInfo.authPersonHash$, kioskPermissions);
        }
      },
    },
    {
      provide: RealtimeDataConsumer,
      deps: [FirebaseMessaging],
      useFactory: (messaging: FirebaseMessaging) => {
        return new FirebaseMessagingRealtimeDataConsumer(messaging);
      },
    },
    {
      provide: RealtimeDirectMessageConsumer,
      deps: [RealtimeDataConsumer],
      useFactory: (dataConsumer: RealtimeDataConsumer) => new RealtimeDirectMessageConsumer(dataConsumer),
    },
    {
      provide: RealtimeNotificationConsumer,
      deps: [RealtimeDataConsumer],
      useFactory: (dataConsumer: RealtimeDataConsumer) => new RealtimeNotificationConsumer(dataConsumer),
    },
    {
      provide: 'googleTagManagerId',
      deps: [AppConfigService],
      useFactory: (appConfigService: AppConfigService) => {
        const config = appConfigService.getGtmConfig();
        return config.id;
      },
    },

    {
      provide: 'googleTagManagerAuth',
      deps: [AppConfigService],
      useFactory: (appConfigService: AppConfigService) => {
        const config = appConfigService.getGtmConfig();
        return config.gtm_auth;
      },
    },
    {
      provide: 'googleTagManagerPreview',
      deps: [AppConfigService],
      useFactory: (appConfigService: AppConfigService) => {
        const config = appConfigService.getGtmConfig();
        return config.gtm_preview;
      },
    },
    {
      provide: BarcodeScanner,
      deps: [ModalOverlayService],
      useFactory: (modalOverlayService: ModalOverlayService) => {
        if (window.MINGA_APP_BROWSER) {
          return new WebcamBarcodeScanner(modalOverlayService);
        } else {
          return new CordovaBarcodeScanner();
        }
      },
    },
    MingaFlexBreakPointsProvider,
    ModalOverlayService,
  ],
  exports: [AppComponent],
  bootstrap: [AppComponent],
})
export class AppModule {}
